// src/utils/mediaCompression.js - Complete media compression utility

/**
 * COMPRESSION STANDARDS:
 * - Product/Service Images: 1000x1000px canvas, 85% quality, 500KB max
 * - Profile Images: 400x400px, 90% quality, 200KB max
 * - Community Images: 1080x1080px, 85% quality, 800KB max
 * - Community Clips: 1080x1080px max, H.264, 2500kbps video/128kbps audio, 10MB max, 10s duration
 * - Home Section Images: 1920x1080px, 85% quality, 1MB max
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';


// ==================== IMAGE COMPRESSION ====================

/**
 * Compress product/service images - fit to 1000x1000 canvas with blur background
 */
export const compressProductImage = async (file, onProgress) => {
  return compressImageToCanvas(file, {
    canvasSize: 1000,
    quality: 0.85,
    maxFileSize: 500 * 1024, // 500KB
    fitMode: 'contain',
    backgroundColor: 'blur',
    onProgress
  });
};

/**
 * Compress profile images - circular crop, 400x400px
 */
export const compressProfileImage = async (file, onProgress) => {
  return compressImageToCanvas(file, {
    canvasSize: 400,
    quality: 0.90,
    maxFileSize: 200 * 1024, // 200KB
    fitMode: 'cover',
    circular: true,
    onProgress
  });
};

/**
 * Compress community images - 1080x1080 with gradient background
 */
export const compressCommunityImage = async (file, onProgress) => {
  return compressImageToCanvas(file, {
    canvasSize: 1080,
    quality: 0.85,
    maxFileSize: 800 * 1024, // 800KB
    fitMode: 'contain',
    backgroundColor: 'gradient',
    onProgress
  });
};

/**
 * Compress home section images - landscape 1920x1080
 */
export const compressHomeImage = async (file, onProgress) => {
  return compressImageToCanvas(file, {
    canvasWidth: 1920,
    canvasHeight: 1080,
    quality: 0.85,
    maxFileSize: 1024 * 1024, // 1MB
    fitMode: 'cover',
    onProgress
  });
};

/**
 * Generate a small grid thumbnail plus a tiny inline blur placeholder (LQIP)
 * for a product/service/post image, alongside the existing full-size
 * compressed upload. Grid cards can then download the thumbnail instead of
 * the full ~1000px image, and show the blur placeholder instantly (it's
 * small enough to store inline in the Firestore doc, no extra network
 * request). Decodes the source once at a small, cheap resolution — separate
 * from the full-size decode compressProductImage does — so this is safe to
 * call independently without affecting the main compressed upload.
 */
export const generateImageVariants = async (file, onProgress) => {
  if (!file || !(file instanceof File)) {
    throw new Error('Invalid file provided');
  }

  const img = await loadImage(file, 400);
  if (onProgress) onProgress(30);

  // Thumbnail: small contain-fit JPEG with the same blurred-fill background
  // treatment as compressProductImage, just smaller.
  const thumbSize = 320;
  const thumbCanvas = document.createElement('canvas');
  thumbCanvas.width = thumbSize;
  thumbCanvas.height = thumbSize;
  const thumbCtx = thumbCanvas.getContext('2d', { alpha: false });
  await drawBackground(thumbCtx, thumbCanvas, img, 'blur');
  const { drawWidth, drawHeight, offsetX, offsetY } = calculateDimensions(
    img.width, img.height, thumbSize, thumbSize, 'contain'
  );
  thumbCtx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
  if (onProgress) onProgress(60);
  const thumbBlob = await compressToSize(thumbCanvas, 0.75, 60 * 1024, onProgress);
  thumbCanvas.width = thumbCanvas.height = 0;
  const thumbnail = new File([thumbBlob], file.name, { type: 'image/jpeg', lastModified: Date.now() });

  // LQIP: tiny, pre-blurred JPEG inlined as a data URL — near-instant paint,
  // no Storage upload or separate network request needed.
  const lqipSize = 24;
  const lqipCanvas = document.createElement('canvas');
  lqipCanvas.width = lqipSize;
  lqipCanvas.height = lqipSize;
  const lqipCtx = lqipCanvas.getContext('2d', { alpha: false });
  lqipCtx.filter = 'blur(2px)';
  lqipCtx.drawImage(img, 0, 0, lqipSize, lqipSize);
  const blurDataURL = lqipCanvas.toDataURL('image/jpeg', 0.5);
  lqipCanvas.width = lqipCanvas.height = 0;

  if (typeof img.close === 'function') img.close();
  if (onProgress) onProgress(100);

  return { thumbnail, blurDataURL };
};

/**
 * Main compression function - handles large files (up to 2GB)
 */
const compressImageToCanvas = async (file, options) => {
  const {
    canvasSize,
    canvasWidth,
    canvasHeight,
    quality = 0.85,
    maxFileSize,
    fitMode = 'contain', // 'contain' | 'cover'
    backgroundColor = 'blur', // 'blur' | 'gradient' | 'solid' | null
    circular = false,
    onProgress
  } = options;

  // Validate file
  if (!file || !(file instanceof File)) {
    throw new Error('Invalid file provided');
  }

  const fileSizeMB = file.size / (1024 * 1024);
  console.log(`📸 Compressing image: ${file.name} (${fileSizeMB.toFixed(2)}MB)`);

  if (onProgress) onProgress(0);

  // For very large files (>100MB), use progressive compression
  if (file.size > 100 * 1024 * 1024) {
    return compressLargeImage(file, options);
  }

  // Calculate dimensions
  const targetWidth = canvasWidth || canvasSize;
  const targetHeight = canvasHeight || canvasSize;

  // Decode only as large as the output actually needs (+30% headroom for
  // cover-mode cropping), capped at MAX_DECODE_DIMENSION. Most upload targets
  // here are 1000px or smaller, so this typically decodes ~2-3x fewer pixels
  // than always decoding to the 2400px ceiling — decode time scales with pixel
  // count, and this was the largest remaining cost on mobile.
  const decodeDimension = Math.min(MAX_DECODE_DIMENSION, Math.round(Math.max(targetWidth, targetHeight) * 1.3));

  // Load image
  const img = await loadImage(file, decodeDimension);
  if (onProgress) onProgress(20);

  const { drawWidth, drawHeight, offsetX, offsetY } = calculateDimensions(
    img.width,
    img.height,
    targetWidth,
    targetHeight,
    fitMode
  );

  if (onProgress) onProgress(40);

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d', { alpha: false });

  // Let the "Compressing image..." progress UI actually paint before the
  // synchronous canvas work below blocks the main thread — otherwise the
  // percentage set above can be queued behind that work and never render,
  // which is what makes this step look frozen rather than just slow.
  await new Promise(resolve => requestAnimationFrame(resolve));

  // Draw background
  if (backgroundColor && fitMode === 'contain') {
    await drawBackground(ctx, canvas, img, backgroundColor);
  }

  if (onProgress) onProgress(60);

  // Draw image
  if (circular) {
    drawCircularImage(ctx, img, targetWidth / 2, targetHeight / 2, Math.min(targetWidth, targetHeight) / 2);
  } else {
    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
  }

  if (onProgress) onProgress(80);

  // Free the decoded bitmap's memory as soon as we're done drawing it
  if (typeof img.close === 'function') img.close();

  // Compress to target file size
  const compressedBlob = await compressToSize(canvas, quality, maxFileSize, onProgress);
  canvas.width = canvas.height = 0;

  if (onProgress) onProgress(100);

  const compressedFile = new File([compressedBlob], file.name, {
    type: 'image/jpeg',
    lastModified: Date.now()
  });

  const finalSizeMB = compressedFile.size / (1024 * 1024);
  console.log(`✅ Compressed: ${fileSizeMB.toFixed(2)}MB → ${finalSizeMB.toFixed(2)}MB`);

  return compressedFile;
};

/**
 * Progressive compression for very large images (2GB capable)
 */
const compressLargeImage = async (file, options) => {
  const { quality = 0.85, maxFileSize, onProgress } = options;
  
  console.log('🔄 Using progressive compression for large file...');
  
  if (onProgress) onProgress(10);

  // Stage 1: Load and downsample to intermediate size (4096px max)
  const img = await loadImage(file);
  if (onProgress) onProgress(30);

  const intermediateSize = 4096;
  const scale = Math.min(1, intermediateSize / Math.max(img.width, img.height));
  
  const intermediateCanvas = document.createElement('canvas');
  intermediateCanvas.width = img.width * scale;
  intermediateCanvas.height = img.height * scale;
  const intermediateCtx = intermediateCanvas.getContext('2d', { alpha: false });
  intermediateCtx.drawImage(img, 0, 0, intermediateCanvas.width, intermediateCanvas.height);
  if (typeof img.close === 'function') img.close();

  if (onProgress) onProgress(50);

  // Stage 2: Compress to target size
  const intermediateBlob = await new Promise(resolve => {
    intermediateCanvas.toBlob(resolve, 'image/jpeg', 0.92);
  });
  intermediateCanvas.width = intermediateCanvas.height = 0;

  // Stage 3: Create final canvas at target dimensions
  const targetWidth = options.canvasWidth || options.canvasSize;
  const targetHeight = options.canvasHeight || options.canvasSize;
  
  const finalImg = await loadImageFromBlob(intermediateBlob);
  if (onProgress) onProgress(70);

  const finalCanvas = document.createElement('canvas');
  finalCanvas.width = targetWidth;
  finalCanvas.height = targetHeight;
  const finalCtx = finalCanvas.getContext('2d', { alpha: false });

  const { drawWidth, drawHeight, offsetX, offsetY } = calculateDimensions(
    finalImg.width,
    finalImg.height,
    targetWidth,
    targetHeight,
    options.fitMode || 'contain'
  );

  if (options.backgroundColor && options.fitMode === 'contain') {
    await drawBackground(finalCtx, finalCanvas, finalImg, options.backgroundColor);
  }

  finalCtx.drawImage(finalImg, offsetX, offsetY, drawWidth, drawHeight);
  if (typeof finalImg.close === 'function') finalImg.close();

  if (onProgress) onProgress(90);

  // Final compression
  const compressedBlob = await compressToSize(finalCanvas, quality, maxFileSize, onProgress);
  finalCanvas.width = finalCanvas.height = 0;

  if (onProgress) onProgress(100);

  return new File([compressedBlob], file.name, {
    type: 'image/jpeg',
    lastModified: Date.now()
  });
};

// ==================== VIDEO COMPRESSION ====================

export const compressCommunityClip = async (file, onProgress) => {
  // Validate file
  if (!file || !(file instanceof File)) {
    throw new Error('Invalid file provided');
  }

  const fileSizeMB = file.size / (1024 * 1024);
  console.log(`🎥 Processing video: ${file.name} (${fileSizeMB.toFixed(2)}MB)`);

  // Validate duration first
  const duration = await getVideoDuration(file);
  if (duration > 10) {
    throw new Error('Video too long. Maximum 10 seconds allowed.');
  }

  if (onProgress) onProgress(10);

  // For files under 10MB, return as-is
  if (file.size <= 10 * 1024 * 1024) {
    console.log('✅ Video already under 10MB, no compression needed');
    if (onProgress) onProgress(100);
    return file;
  }

  // For files under 50MB, try client-side compression
  if (file.size <= 50 * 1024 * 1024) {
    try {
      return await compressVideoClient(file, {
        maxWidth: 1080,
        maxHeight: 1080,
        videoBitrate: 2500,
        audioBitrate: 128,
        maxFileSize: 10 * 1024 * 1024,
        onProgress
      });
    } catch (error) {
      console.warn('⚠️ Client-side compression failed, trying server-side...', error);
      // Fall through to server-side compression
    }
  }

  // For files >50MB or failed client-side, use server-side compression
  if (file.size <= 2 * 1024 * 1024 * 1024) { // 2GB max
    try {
      if (onProgress) onProgress(20);
      return await compressVideoServer(file, onProgress);
    } catch (error) {
      console.error('❌ Server-side compression failed:', error);
      throw new Error(`Video compression failed: ${error.message}`);
    }
  }

  throw new Error('Video file too large. Maximum 2GB.');
};

// Add new server-side compression function:
const compressVideoServer = async (file, onProgress) => {
  console.log('🌐 Using server-side compression...');

  try {
    // Create FormData
    const formData = new FormData();
    formData.append('file', file);

    if (onProgress) onProgress(30);

    // Upload to server for compression
    const response = await fetch(`${API_URL}/api/compress-media`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Server error: ${response.status}`);
    }

    if (onProgress) onProgress(80);

    // Get compressed video
    const blob = await response.blob();
    const compressedFile = new File([blob], file.name, {
      type: 'video/mp4',
      lastModified: Date.now()
    });

    // Validate result
    if (compressedFile.size > 10 * 1024 * 1024) {
      throw new Error('Compressed video still too large. Try a shorter clip.');
    }

    if (onProgress) onProgress(100);

    const originalSizeMB = file.size / (1024 * 1024);
    const finalSizeMB = compressedFile.size / (1024 * 1024);
    console.log(`✅ Server compression complete: ${originalSizeMB.toFixed(2)}MB → ${finalSizeMB.toFixed(2)}MB`);

    return compressedFile;

  } catch (error) {
    console.error('❌ Server compression error:', error);
    throw error;
  }
};

// Also add server-side image compression for very large images:
export const compressLargeImageServer = async (file, onProgress) => {
  console.log('🌐 Using server-side image compression...');

  try {
    const formData = new FormData();
    formData.append('file', file);

    if (onProgress) onProgress(30);

    const response = await fetch(`${API_URL}/api/compress-media`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Server error: ${response.status}`);
    }

    if (onProgress) onProgress(80);

    const blob = await response.blob();
    const compressedFile = new File([blob], file.name, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });

    if (onProgress) onProgress(100);

    return compressedFile;

  } catch (error) {
    console.error('❌ Server image compression error:', error);
    throw error;
  }
};

/**
 * Client-side video compression using canvas + MediaRecorder
 */
const compressVideoClient = async (file, options) => {
  const { maxWidth, maxHeight, videoBitrate, audioBitrate, maxFileSize, onProgress } = options;

  console.log('🔄 Compressing video client-side...');

  if (onProgress) onProgress(20);

  // Load video
  const video = document.createElement('video');
  video.src = URL.createObjectURL(file);
  video.muted = true;
  
  await new Promise((resolve, reject) => {
    video.onloadedmetadata = resolve;
    video.onerror = reject;
  });

  if (onProgress) onProgress(40);

  // Calculate dimensions
  const scale = Math.min(1, maxWidth / video.videoWidth, maxHeight / video.videoHeight);
  const width = Math.floor(video.videoWidth * scale);
  const height = Math.floor(video.videoHeight * scale);

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  // Setup MediaRecorder
  const stream = canvas.captureStream(30);
  const mediaRecorder = new MediaRecorder(stream, {
    mimeType: 'video/webm;codecs=vp9',
    videoBitsPerSecond: videoBitrate * 1000,
    audioBitsPerSecond: audioBitrate * 1000
  });

  const chunks = [];
  mediaRecorder.ondataavailable = (e) => chunks.push(e.data);

  if (onProgress) onProgress(60);

  // Record video
  await new Promise((resolve) => {
    mediaRecorder.onstop = resolve;
    mediaRecorder.start();

    video.play();
    video.onended = () => {
      mediaRecorder.stop();
      video.pause();
    };

    // Draw frames
    const drawFrame = () => {
      if (video.paused || video.ended) return;
      ctx.drawImage(video, 0, 0, width, height);
      requestAnimationFrame(drawFrame);
    };
    drawFrame();
  });

  if (onProgress) onProgress(90);

  // Create blob
  const blob = new Blob(chunks, { type: 'video/webm' });
  const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.webm'), {
    type: 'video/webm',
    lastModified: Date.now()
  });

  // Check if within size limit
  if (compressedFile.size > maxFileSize) {
    throw new Error(`Compressed video still too large (${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB). Try a shorter clip or lower quality source.`);
  }

  if (onProgress) onProgress(100);

  const originalSizeMB = file.size / (1024 * 1024);
  const finalSizeMB = compressedFile.size / (1024 * 1024);
  console.log(`✅ Video compressed: ${originalSizeMB.toFixed(2)}MB → ${finalSizeMB.toFixed(2)}MB`);

  return compressedFile;
};

// ==================== HELPER FUNCTIONS ====================

// Cap how large a bitmap we ever decode to. Modern phone cameras shoot
// 20-200MP photos — decoding those at full resolution via an <img>/canvas
// (as opposed to a resized createImageBitmap) allocates hundreds of MB of
// raw pixel memory and is what makes compression appear to hang/freeze on
// mobile. No compression target here needs more than ~2400px on a side.
const MAX_DECODE_DIMENSION = 2400;
const supportsImageBitmap = typeof window !== 'undefined' && 'createImageBitmap' in window;

/**
 * Load image from File
 * Prefers createImageBitmap with a resize hint (decodes already-downscaled,
 * off the main thread on most browsers) and falls back to an <img> element
 * for browsers/formats that don't support it.
 */
const loadImage = async (file, resizeWidth = MAX_DECODE_DIMENSION) => loadWithRetry(file, resizeWidth);

/**
 * Load image from Blob (same rationale as loadImage above)
 */
const loadImageFromBlob = async (blob, resizeWidth = MAX_DECODE_DIMENSION) => loadWithRetry(blob, resizeWidth);

// The exact same photo can decode fine one moment and fail the next — this is
// mobile memory pressure, not a real format problem: createImageBitmap (and
// <img> decode) can transiently throw when the browser is low on memory, and
// succeed a moment later once something's been freed. So a single decode
// failure across all tiers isn't treated as final — wait briefly (giving the
// browser a chance to reclaim memory/finish GC) and run the whole chain again
// before surfacing an error to the user.
const loadWithRetry = async (source, resizeWidth) => {
  const attempt = async () => {
    const bitmap = await loadViaImageBitmap(source, resizeWidth);
    return bitmap ?? loadViaImgElement(source);
  };
  const delays = [400, 900]; // increasing backoff — gives the browser more time to reclaim memory on later tries
  let lastErr;
  for (let i = 0; i <= delays.length; i++) {
    try {
      return await attempt();
    } catch (err) {
      lastErr = err;
      if (i >= delays.length) break;
      console.warn(`Image decode failed, retrying (${i + 1}/${delays.length}) after a short delay:`, err);
      await new Promise(resolve => setTimeout(resolve, delays[i]));
    }
  }
  throw lastErr;
};

// Three-tier decode, from cheapest/safest to most expensive:
//  1. createImageBitmap with a resize hint — decodes already-downscaled, off
//     the main thread on most browsers. The common case.
//  2. createImageBitmap with just the resize width, no quality hint — some
//     browsers (and some formats, notably iPhone HEIC photos on certain
//     Android/older Safari builds) reject the full resize dictionary even
//     though createImageBitmap itself works fine. Still bounded, still
//     off-thread, avoids ever decoding a huge source photo at native res.
//  3. <img> element — last resort for browsers with no createImageBitmap at
//     all. This is the one that can actually hang on a 20-200MP phone photo,
//     which is why it's tried last instead of first.
// `imageOrientation: 'from-image'` on every createImageBitmap call is required
// — without it, portrait phone photos come out sideways/upside-down, since
// createImageBitmap ignores the shot's EXIF rotation flag by default (unlike
// <img>, which always auto-rotates for display).
const loadViaImageBitmap = async (source, resizeWidth) => {
  if (!supportsImageBitmap) return null;
  try {
    return await createImageBitmap(source, {
      resizeWidth,
      // 'low' (nearest-neighbor-ish) decodes noticeably faster than 'medium'
      // on mobile GPUs, and the quality difference is invisible once this
      // gets JPEG-compressed and drawn again at the final target size anyway.
      resizeQuality: 'low',
      imageOrientation: 'from-image',
    });
  } catch (err) {
    console.warn('createImageBitmap with resize hint failed, retrying without it:', err);
  }
  // Still bounded — an unbounded retry here defeats the point of tier 1 and is
  // exactly what was making large (post-cap-raise) source photos crash/hang
  // instead of cleanly failing: a 40-50MB, 40-100MP phone photo decoded at
  // native resolution can allocate several hundred MB, which is enough to
  // kill the tab outright on many phones rather than throw a catchable error.
  try {
    return await createImageBitmap(source, { resizeWidth, imageOrientation: 'from-image' });
  } catch (err) {
    console.warn('createImageBitmap failed outright, falling back to <img> decode:', err);
    return null;
  }
};

const loadViaImgElement = (source) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(source);
  // Revoking right after load/error doesn't affect the already-decoded <img>
  // (revoking only stops *future* dereferences of the URL) — but leaving these
  // un-revoked leaks one blob per photo for the rest of the session, adding up
  // to real memory pressure across several uploads, which is exactly the kind
  // of thing that makes decoding intermittently fail (works the first few
  // times, then starts failing) even for the same, perfectly valid photo.
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  // The native onerror handler fires with a bare Event, not an Error — it has
  // no .message, so callers upstream fall through to a generic "failed to
  // upload" alert with no way to tell what actually went wrong. This is the
  // last-resort decoder (createImageBitmap already failed twice by this point),
  // so a load failure here usually means the browser just can't decode this
  // photo's format at all (e.g. HEIC on some Android builds).
  img.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error("This photo's format isn't supported by your browser. Try taking a new photo or converting it to JPEG/PNG first."));
  };
  img.src = url;
});

/**
 * Calculate dimensions for contain/cover fit
 */
const calculateDimensions = (srcWidth, srcHeight, targetWidth, targetHeight, fitMode) => {
  let scale;
  
  if (fitMode === 'cover') {
    scale = Math.max(targetWidth / srcWidth, targetHeight / srcHeight);
  } else { // contain
    scale = Math.min(targetWidth / srcWidth, targetHeight / srcHeight);
  }

  const drawWidth = srcWidth * scale;
  const drawHeight = srcHeight * scale;
  const offsetX = (targetWidth - drawWidth) / 2;
  const offsetY = (targetHeight - drawHeight) / 2;

  return { drawWidth, drawHeight, offsetX, offsetY, scale };
};

/**
 * Draw background (blur, gradient, or solid)
 */
const drawBackground = async (ctx, canvas, img, type) => {
  if (type === 'blur') {
    // Blurring at full canvas resolution (e.g. 1000x1000 = 1M px) is what makes
    // this step appear to hang on mobile — most mobile browsers rasterize canvas
    // filter blur on the CPU, and the cost scales with pixel count, not visual
    // smoothness. Blur cost is roughly a matter of how many pixels it processes:
    // shrink to a tiny canvas first, blur that (a fraction of the pixels), then
    // scale the already-blurred result back up — visually indistinguishable,
    // dramatically cheaper.
    const smallSize = 96;
    const smallCanvas = document.createElement('canvas');
    smallCanvas.width = smallSize;
    smallCanvas.height = smallSize;
    const smallCtx = smallCanvas.getContext('2d', { alpha: false });
    smallCtx.drawImage(img, 0, 0, smallSize, smallSize);

    const blurCanvas = document.createElement('canvas');
    blurCanvas.width = smallSize;
    blurCanvas.height = smallSize;
    const blurCtx = blurCanvas.getContext('2d', { alpha: false });
    blurCtx.filter = 'blur(6px)';
    blurCtx.drawImage(smallCanvas, 0, 0);

    ctx.drawImage(blurCanvas, 0, 0, smallSize, smallSize, 0, 0, canvas.width, canvas.height);

    // Setting width/height to 0 immediately releases a canvas's backing pixel
    // buffer in most engines instead of waiting on GC — with several image
    // uploads happening in a session (or several sections' images at save
    // time), leftover canvas memory is exactly the kind of pressure that makes
    // decode/compression intermittently fail a turn later.
    smallCanvas.width = smallCanvas.height = 0;
    blurCanvas.width = blurCanvas.height = 0;
  } else if (type === 'gradient') {
    // Gradient background
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#1a1a1a');
    gradient.addColorStop(1, '#2d2d2d');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  } else if (type && type.startsWith('#')) {
    // Solid color
    ctx.fillStyle = type;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
};

/**
 * Draw circular image
 */
const drawCircularImage = (ctx, img, centerX, centerY, radius) => {
  ctx.save();
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  
  const size = radius * 2;
  ctx.drawImage(img, centerX - radius, centerY - radius, size, size);
  
  ctx.restore();
};

/**
 * Compress canvas to target file size
 */
const compressToSize = async (canvas, initialQuality, maxFileSize, onProgress) => {
  let quality = initialQuality;
  let blob;
  let attempts = 0;
  const maxAttempts = 5;

  while (attempts < maxAttempts) {
    blob = await new Promise(resolve => {
      canvas.toBlob(resolve, 'image/jpeg', quality);
    });

    if (blob.size <= maxFileSize || attempts >= maxAttempts - 1) {
      break;
    }

    quality -= 0.1;
    attempts++;
    
    if (onProgress && attempts < maxAttempts) {
      const progress = 80 + (attempts / maxAttempts) * 15;
      onProgress(progress);
    }
  }

  return blob;
};

/**
 * Get video duration
 */
const getVideoDuration = (file) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = reject;
    video.src = URL.createObjectURL(file);
  });
};

/**
 * Read a File/Blob as a data URL, as a proper Promise.
 *
 * FileReader's `onloadend` fires on BOTH success and failure — every upload
 * site in this app used to treat it as a success callback with no error
 * check, so a failed read (more likely under the same mobile memory pressure
 * documented above) silently produced a null/empty preview with no error
 * shown at all. The user just saw nothing happen and had no way to know why,
 * which is exactly what made "try uploading again a few times" the only
 * recovery — this makes that failure surface as a real, catchable error.
 */
export const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    if (!reader.result) {
      reject(new Error('Failed to read image file. Please try again.'));
      return;
    }
    resolve(reader.result);
  };
  reader.onerror = () => reject(new Error('Failed to read image file. Please try again.'));
  reader.readAsDataURL(file);
});

/**
 * Validate image file
 */
export const validateImageFile = (file, maxSizeMB = 2048) => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  const validExtensions = /\.(jpe?g|png|webp|heic|heif)$/i;

  // Some mobile browsers/camera apps report an empty or generic MIME type
  // (e.g. 'application/octet-stream') for photos, so fall back to the file
  // extension instead of rejecting the upload outright.
  const hasValidType = validTypes.includes(file.type);
  const hasValidExtension = validExtensions.test(file.name || '');

  if (!hasValidType && !hasValidExtension) {
    throw new Error('Invalid image type. Supported: JPEG, PNG, WebP, HEIC');
  }

  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > maxSizeMB) {
    throw new Error(`Image too large (${fileSizeMB.toFixed(2)}MB). Maximum: ${maxSizeMB}MB`);
  }

  return true;
};

/**
 * Validate video file
 */
export const validateVideoFile = (file, maxSizeMB = 500, maxDuration = 10) => {
  const validTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
  
  if (!validTypes.includes(file.type)) {
    throw new Error('Invalid video type. Supported: MP4, MOV, WebM');
  }

  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > maxSizeMB) {
    throw new Error(`Video too large (${fileSizeMB.toFixed(2)}MB). Maximum: ${maxSizeMB}MB`);
  }

  return getVideoDuration(file).then(duration => {
    if (duration > maxDuration) {
      throw new Error(`Video too long (${duration.toFixed(1)}s). Maximum: ${maxDuration}s`);
    }
    return true;
  });
};